import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, ref } from 'vue';

import { useArbitrage } from '@/helpers/composables/useArbitrage';

import { diffInDays } from '@/helpers/date-helper';
import { shouldClickMask, clickMaskUrl } from '@/components/click-masking/click-masking-config';
import { BuyboxParam, getTranslatableForOfferGraphql, OfferLocation } from '@/helpers/offer-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';

import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { MonetizationType, PresentationType, ObjectType } from '@/@types/graphql-types';

import { ClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { SponsoredClickoutsHelper } from '@/features/sponsored-recommendations/helpers/sponsored-clickouts-helper';

import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIconSquare from '@/features/ui/components/ProviderIconSquare.vue';
import { ClickoutContentType } from '@/interfaces/snowplow/clickout-context';
import { type MaybeRef, toValue, useMounted } from '@vueuse/core';
import { getClickoutTrackingPlacement, OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';

import { useProviderPromotion } from '@/helpers/provider-promotion';
import { faClock } from '@fortawesome/free-solid-svg-icons';

import { useAppStore } from '@/stores/pinia/useAppStore';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useActivateExperiment } from '@/libraries/experiment';
import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { isJWTVPricingExpShouldBeActive, useJWTVPricingExp } from '@/components/experiments/JWTVPricingExp';
import { useGridBuyboxRedesignCTAExp } from '@/components/experiments/GridBuyboxRedesignExps/GridBuyboxRedesignCTAExp';
import { useGridMoreInfoExp } from '@/components/experiments/GridBuyboxRedesignExps/GridMoreInfoExp';
import { useRoute } from 'vue-router/composables';

interface BuyBoxOfferProps {
	/**  Bundles get a different iconUrl, this property is added to the offer in OfferRow */
	offer: TitleOfferFragment & { iconUrl?: string; bundleId?: number };
	promoted?: boolean;
	objectType: ObjectType;
	hidePresentationTypeLabel?: boolean;
	title?: TitleDetail;
	hideLeavingShowIcon?: boolean;
	additionalContexts?: SnowplowContext[];
	inline?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GridOffer',
  props: {
    offer: null,
    promoted: { type: Boolean, default: false },
    objectType: null,
    hidePresentationTypeLabel: { type: Boolean, default: false },
    title: null,
    hideLeavingShowIcon: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    inline: { type: Boolean }
  },
  emits: ["clickOut"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		data: {
			buyboxOffer: BuyBoxOfferProps['offer'];
			isBundle: boolean;
			isLeavingIconShown: boolean;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { offer: TitleOfferFragment & { iconUrl?: string; bundleId?: number }, promoted: boolean, objectType: ObjectType, hidePresentationTypeLabel: boolean, title?: TitleDetail, hideLeavingShowIcon: boolean, additionalContexts: SnowplowContext[], inline?: boolean };

const playerModalByProvider: Record<string, { cmp: () => Promise<any> }> = {
	plexplayer: {
		cmp: () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue'),
	},
	justwatchtv: {
		cmp: () => import('@/ui-components/video-player-modals/JwTVModal.vue'),
	},
	ottstudioplayer: {
		cmp: () => import('@/ui-components/video-player-modals/OttPlayerModal.vue'),
	},
};





const { country, webLocale } = storeToRefs(useLanguageStore());
const { isLoggedIn } = storeToRefs(useUserStore());
const { xServeTrack } = storeToRefs(useAppStore());

// EXP_JWTV_PRICING
const jwtvPricingExpCondition = computed(() => isJWTVPricingExpShouldBeActive(props.offer));
const { activeVariant } = useActivateExperiment('EXP_JWTV_PRICING', jwtvPricingExpCondition);
const { activePromo } = useJWTVPricingExp(); // just to make sure the experiment is activated
const jwtvPriceOverride = computed(() => {
	const retailPrice = props.offer.retailPriceValue;
	if (activePromo.value && retailPrice && activeVariant.value) {
		return activePromo.value?.calculatePrice?.(retailPrice) ?? retailPrice;
	}
	return null;
});
// EXP_JWTV_PRICING

const { isPromotionOffer, providerPromotion: providerPromo } = useProviderPromotion();

const isProviderPromo = computed(() => !props.offer.bundleId && isPromotionOffer(props.offer));

const isModalProvider = computed(() =>
	['plexplayer', 'justwatchtv', 'ottstudioplayer'].includes(props.offer.package.technicalName)
);

const _clickoutType = computed(() => {
	if (isModalProvider.value) return 'video_player';
	return props.promoted ? 'promoted' : 'regular';
});
const { clickoutType, placement } = getClickoutTrackingPlacement(_clickoutType.value);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);

const shouldMaskClicks = computed(() => shouldClickMask(props.offer.package.packageId, country.value));

const isBannerBuyBox = inject('isBannerBuyBox', false);
const isMiddleBuyBox = inject('isMiddleBuyBox', false);
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const titleSourceId = inject<string | null>('titleSourceId', null);
const noOffer = inject<boolean | undefined>('noOffer', undefined);

const jwRawEntityId = computed(() => episodeEntityId ?? props.title?.id);

const { isArbitrating, withArbitrageTracking, trackTagEvent } = useArbitrage({ entityId: jwRawEntityId });

const offerLocation = computed(() => {
	if (toValue(isBannerBuyBox)) return OfferLocation.BANNER_BUYBOX;
	if (toValue(isMiddleBuyBox)) return OfferLocation.MIDDLE_BUYBOX;
	if (props.promoted) return OfferLocation.PROMOTED_BUYBOX;

	return undefined;
});

const buyboxIndex = inject<MaybeRef<number | undefined>>('buyboxIndex', undefined);

// the UCT offer url is massive.
// in some cases we have 300 links on the page that explodes the HTML.
// if we exceed 2mb page size, google will stop the crawl - so it's important to stay under 2mb.
//
// currently we only hide the offer url:
// - during SSR
// - on guide article pages
// - NOT on the 1st article item
const route = useRoute();
const isMounted = useMounted();
const hideOfferUrl = computed(
	() =>
		['app.editorial.subdomain.article'].includes(route.name as string) &&
		!isMounted.value &&
		(toValue(buyboxIndex) || 0) >= 1
);

const isLeavingIconShown = computed(() => {
	return (
		!props.promoted &&
		(isLoggedIn.value as boolean) &&
		!!props.offer.availableTo &&
		diffInDays(new Date(props.offer.availableTo)) < 30
	);
});

function clickoutBase(url: ClickoutUrl) {
	return url
		.setForTracking('offerLocation', offerLocation.value)
		.updateContext(
			'ClickoutContextGraphql',
			'contentType',
			isLeavingIconShown.value ? ClickoutContentType.LeavingSoon : undefined
		)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
		.updateContext('ClickoutContextGraphql', 'noOffer', noOffer)
		.updateContext('ClickoutContextGraphql', 'rowNumber', toValue(buyboxIndex))
		.set('uct_country', country.value)
		.set('uct_buybox', props.promoted ? BuyboxParam.Promoted : BuyboxParam.Normal)
		.setTitleSourceId(titleSourceId)
		.setCanary(xServeTrack.value)
		.setGenre(props.title?.content.genres);
}

const { setGridRedesignTags } = useGridBuyboxRedesignCTAExp();
const { setGridMoreInfoTags } = useGridMoreInfoExp();

const offerUrl = computed(() => {
	let clickoutUrl = new ClickoutUrl(props.offer);

	if (isProviderPromo.value && providerPromo.value?.url) {
		const offer = { ...props.offer, standardWebURL: providerPromo.value.url };
		clickoutUrl = new ClickoutUrl(offer).skipRedirect();
	}

	if (shouldMaskClicks.value) {
		const maskedUrl = clickMaskUrl(props.offer.standardWebURL!);
		const offer = { ...props.offer, standardWebURL: maskedUrl };
		clickoutUrl = new ClickoutUrl(offer).skipRedirect();
	}

	if (isArbitrating.value && ['amp', 'atp'].includes(props.offer.package.shortName)) {
		const arbitrageUrl = withArbitrageTracking({
			offer: () => props.offer,
			title: () => props.title,
			placement: 'ro',
		});
		if (arbitrageUrl) {
			return arbitrageUrl.apply(clickoutBase);
		}
	}

	const url = clickoutUrl
		.apply(clickoutBase)
		.apply(u => setGridRedesignTags(u, 'ro', props.offer.package.shortName, props.inline))
		.apply(u => setGridMoreInfoTags(u, 'ro', props.offer.package.shortName, props.inline));

	if (titleContext.value) {
		url.addContexts(titleContext.value).updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value);
	}

	// Amazon streaming bounty redirects

	if ([9, 119].includes(props.offer.package.packageId)) {
		return url.set('r', url.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	return url;
});

const offerLabel = computed(() => {
	if (isProviderPromo.value && providerPromo.value?.offerText) return providerPromo.value.offerText;

	const translation = getTranslatableForOfferGraphql(props.offer, props.objectType);

	if (!translation) return props.offer.retailPrice;

	if (props.offer.monetizationType === MonetizationType.FlatrateAndBuy) {
		return translation + (props.offer?.retailPrice ?? '');
	}

	return translation;
});

const presentationTypeLabel = computed(() => {
	switch (props.offer.presentationType) {
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'DVD';
		case PresentationType.Bluray:
		case PresentationType.Bluray_4K:
			return 'Blu-Ray';
		default:
			return '';
	}
});

const isSponsoredOffer = computed(() =>
	SponsoredClickoutsHelper.isSponsoredOfferGraphql(props.offer.package.packageId, webLocale.value)
		? 'nofollow sponsored noopener'
		: 'nofollow noopener'
);

/* OFFER IMPRESSION TRACKING */
const observeVisibilityOptions = computed(() => {
	return {
		...observableOptions,
		callback: onVisibilityChanged,
	};
});

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

function onVisibilityChanged(isVisible: boolean) {
	onIntersection(offerUrl.value, isVisible);
}

function handleClickOut() {
	trackTagEvent(() => props.offer);

	emit('clickOut', {
		buyboxOffer: props.offer,
		isBundle: !!props.offer.iconUrl,
		isLeavingIconShown: isLeavingIconShown.value,
	});
}

//START AVOD
const { openRentalModal } = useRentalModal();
function openModal() {
	if (!isModalProvider.value) return;
	const modalData = playerModalByProvider[props.offer.package.technicalName];

	if (isJWTVRentalOffer(props.offer)) {
		// we trigger clickout first to get the final link context id as it's used to link events especially after page redirects.
		trackClickout(offerUrl.value);
		openRentalModal({
			offer: props.offer,
			title: props.title!,
			clickoutUrl: offerUrl.value,
			expPromoCode: activeVariant.value ? activePromo.value : null,
		});
		return;
	}

	ModalHelper.openModal(
		modalData.cmp,
		{ offer: props.offer, title: props.title },
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);

	trackClickout(offerUrl.value);
}
//END AVOD

return { __sfc: true,playerModalByProvider, props, emit, country, webLocale, isLoggedIn, xServeTrack, jwtvPricingExpCondition, activeVariant, activePromo, jwtvPriceOverride, isPromotionOffer, providerPromo, isProviderPromo, isModalProvider, _clickoutType, clickoutType, placement, episodeEntityId, shouldMaskClicks, isBannerBuyBox, isMiddleBuyBox, titleContext, titleSourceId, noOffer, jwRawEntityId, isArbitrating, withArbitrageTracking, trackTagEvent, offerLocation, buyboxIndex, route, isMounted, hideOfferUrl, isLeavingIconShown, clickoutBase, setGridRedesignTags, setGridMoreInfoTags, offerUrl, offerLabel, presentationTypeLabel, isSponsoredOffer, observeVisibilityOptions, onIntersection, onVisibilityChanged, handleClickOut, openRentalModal, openModal, ClickoutAnchor, ProviderIconSquare, faClock }
}

})